import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  limit,
  Timestamp,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { httpsCallable, functions } from '@/lib/functions';
import { retryWithBackoff } from '@/utils/retry';
import {
  getCachedWeatherForecast,
  cacheWeatherForecast,
  normalizeWeatherCacheParams,
  getCachedGeocode,
  cacheGeocode,
} from '@/utils/weatherCache';
import { trackSanitizationIntercept } from '@/services/sanitizationTelemetry';
import {
  coordsWithinCountryBounds,
  geocodeContextMatchesCountryCode,
  getCountryCodeFromDestination,
} from '@/utils/geocodeHelpers';

export type WeatherAdvisory = {
  id: string;
  location: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  type: 'weather' | 'severe_weather' | 'storm' | 'flood' | 'heat' | 'cold' | 'air_quality';
  title: string;
  description: string;
  issuedAt: Date;
  expiresAt?: Date;
  source: 'openweather' | 'manual';
  isRead?: boolean;
};

export type FirestoreWeatherAdvisory = {
  location: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
  severity: string;
  type: string;
  title: string;
  description: string;
  issuedAt: Timestamp | string;
  expiresAt?: Timestamp | string | null;
  source: string;
  isRead?: boolean;
};

export type WeatherSubscriptionStatus = {
  source: 'weather';
  state: 'ready' | 'error';
  message?: string;
  code?: string;
};

export type AdvisoryFetchStatus = 'updated' | 'noCoverage' | 'sourceUnavailable' | 'rateLimited';

const weatherSeverityOrder: Record<WeatherAdvisory['severity'], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};
const weatherAdvisoryTypes: ReadonlySet<WeatherAdvisory['type']> = new Set([
  'weather',
  'severe_weather',
  'storm',
  'flood',
  'heat',
  'cold',
  'air_quality',
]);

/**
 * Subscribe to weather advisories for a location
 */
export const subscribeToWeatherAdvisories = (
  location: { latitude: number; longitude: number } | { countryCode: string },
  callback: (advisories: WeatherAdvisory[]) => void,
  onStatus?: (status: WeatherSubscriptionStatus) => void
): Unsubscribe => {
  const advisoriesRef = collection(db, 'advisories');
  
  // Build query based on location type
  let q;
  if ('latitude' in location) {
    // Query by coordinates (simplified - would need geohash or radius query in production)
    q = query(
      advisoriesRef,
      limit(150)
    );
  } else {
    q = query(
      advisoriesRef,
      where('countryCode', '==', location.countryCode),
      limit(150)
    );
  }

  let unsubscribeFn: (() => void) | null = null;
  let isCleanedUp = false;

  try {
    unsubscribeFn = onSnapshot(
      q,
      (snapshot) => {
        // Don't process updates if already cleaned up
        if (isCleanedUp) {
          return;
        }

        try {
          const advisories: WeatherAdvisory[] = snapshot.docs
            .map((doc): WeatherAdvisory | null => {
              const data = doc.data() as FirestoreWeatherAdvisory;
              const expiresAt = data.expiresAt
                ? data.expiresAt instanceof Timestamp
                  ? data.expiresAt.toDate()
                  : new Date(data.expiresAt)
                : undefined;

              // Filter out expired advisories
              if (expiresAt && expiresAt < new Date()) {
                return null;
              }

              return {
                id: doc.id,
                location: data.location,
                countryCode: data.countryCode,
                latitude: data.latitude,
                longitude: data.longitude,
                severity: data.severity as WeatherAdvisory['severity'],
                type: data.type as WeatherAdvisory['type'],
                title: data.title,
                description: data.description,
                issuedAt:
                  data.issuedAt instanceof Timestamp
                    ? data.issuedAt.toDate()
                    : new Date(data.issuedAt),
                expiresAt,
                source: data.source as WeatherAdvisory['source'],
                isRead: data.isRead || false,
              };
            })
            .filter(
              (adv): adv is WeatherAdvisory =>
                adv !== null && adv !== undefined && weatherAdvisoryTypes.has(adv.type)
            );

          // Sort by severity (critical first) then by date
          advisories.sort((a, b) => {
            const severityDiff = weatherSeverityOrder[a.severity] - weatherSeverityOrder[b.severity];
            if (severityDiff !== 0) return severityDiff;
            return b.issuedAt.getTime() - a.issuedAt.getTime();
          });

          callback(advisories);
          onStatus?.({ source: 'weather', state: 'ready' });
        } catch (error) {
          console.error('[Weather] Error processing snapshot:', error);
          onStatus?.({
            source: 'weather',
            state: 'error',
            message: error instanceof Error ? error.message : 'Failed to process weather advisories',
          });
        }
      },
      (error) => {
        // Don't process errors if already cleaned up
        if (isCleanedUp) {
          return;
        }

        console.error('[Weather] Error subscribing to weather advisories:', error);
        const errorCode = (error as any)?.code;
        onStatus?.({
          source: 'weather',
          state: 'error',
          code: errorCode,
          message: (error as any)?.message || 'Failed to subscribe to weather advisories',
        });
      }
    );
  } catch (error) {
    console.error('[Weather] Error setting up subscription:', error);
    // Return a no-op cleanup function if setup fails
    return () => {};
  }

  return () => {
    if (isCleanedUp) {
      return; // Already cleaned up, prevent double cleanup
    }
    isCleanedUp = true;

    if (unsubscribeFn) {
      try {
        unsubscribeFn();
      } catch (error) {
        // Silently ignore cleanup errors - they're often due to Firestore internal state
      } finally {
        unsubscribeFn = null; // Clear reference after cleanup
      }
    }
  };
};

/**
 * Fetch weather advisories from OpenWeatherMap (via cloud function)
 * This would typically be called by a cloud function that runs periodically
 */
export const fetchWeatherAdvisories = async (
  location: { latitude: number; longitude: number } | { city: string; countryCode: string }
): Promise<{ success: boolean; status?: AdvisoryFetchStatus; created?: number; error?: string }> => {
  try {
    const callFetchWeather = httpsCallable<
      typeof location,
      { success: boolean; status?: AdvisoryFetchStatus; created?: number; error?: string }
    >(
      functions,
      'fetchWeatherAdvisories'
    );

    const result = await callFetchWeather(location);
    return {
      success: result.data.success,
      status: result.data.status,
      created: result.data.created,
      error: result.data.error,
    };
  } catch (error) {
    console.error('[Weather] Error fetching weather advisories:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch weather advisories',
    };
  }
};

/**
 * Mark advisory as read
 */
export const markAdvisoryAsRead = async (advisoryId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const advisoryRef = doc(db, 'advisories', advisoryId);
    await getDoc(advisoryRef).then(async (docSnap) => {
      if (docSnap.exists()) {
        // Note: In production, you'd update a user-specific read status
        // For now, this is a placeholder
      }
    });

    return { success: true };
  } catch (error) {
    console.error('[Weather] Error marking advisory as read:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to mark advisory as read',
    };
  }
};

// Weather Forecast Types
export type WeatherForecast = {
  location: {
    city: string;
    country: string;
    latitude: number;
    longitude: number;
  };
  current: {
    temperature: number;
    feelsLike: number;
    condition: string;
    description: string;
    icon: string;
    humidity: number;
    windSpeed: number;
    windDirection: number;
    pressure: number;
    visibility?: number;
    uvIndex?: number;
  };
  forecast: Array<{
    date: string;
    temperature: {
      min: number;
      max: number;
      day: number;
      night: number;
    };
    condition: string;
    description: string;
    icon: string;
    humidity: number;
    windSpeed: number;
    precipitation: {
      probability: number;
      amount?: number;
    };
    sunrise?: string;
    sunset?: string;
  }>;
  alerts?: Array<{
    severity: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    description: string;
    start: string;
    end: string;
  }>;
};

export type WeatherForecastRequest = {
  latitude: number;
  longitude: number;
  city?: string;
  countryCode?: string;
  days?: number;
  targetDate?: string; // ISO date string - forecast will start from this date (or day before if trip is in future)
};

/**
 * Fetch weather forecast for a location
 * Uses caching and retry logic for improved reliability
 */
export const fetchWeatherForecast = async (
  request: WeatherForecastRequest
): Promise<WeatherForecast | null> => {
  if (typeof request.city === 'string' && request.city.trim().length === 0) {
    trackSanitizationIntercept({
      scope: 'weather_forecast',
      reason: 'empty_city',
      droppedFields: ['city'],
    });
  }

  if (
    typeof request.countryCode === 'string' &&
    request.countryCode.trim().length > 0 &&
    !/^[A-Za-z]{2}$/.test(request.countryCode.trim())
  ) {
    trackSanitizationIntercept({
      scope: 'weather_forecast',
      reason: 'invalid_country_code',
      droppedFields: ['countryCode'],
    });
  }

  const normalizedCity = typeof request.city === 'string' && request.city.trim().length > 0
    ? request.city.trim()
    : undefined;
  const normalizedCountryCodeRaw = typeof request.countryCode === 'string'
    ? request.countryCode.trim().toUpperCase()
    : undefined;
  const normalizedCountryCode =
    normalizedCountryCodeRaw && /^[A-Z]{2}$/.test(normalizedCountryCodeRaw)
      ? normalizedCountryCodeRaw
      : undefined;
  const requestPayload: WeatherForecastRequest = {
    latitude: request.latitude,
    longitude: request.longitude,
    ...(typeof request.days === 'number' ? { days: request.days } : {}),
    ...(typeof request.targetDate === 'string' && request.targetDate.length > 0
      ? { targetDate: request.targetDate }
      : {}),
    ...(normalizedCity ? { city: normalizedCity } : {}),
    ...(normalizedCountryCode ? { countryCode: normalizedCountryCode } : {}),
  };

  try {
    // Normalize params so cache key is consistent across all call sites (unified cache hits)
    const { lat, lon, targetDate, days } = normalizeWeatherCacheParams(
      requestPayload.latitude,
      requestPayload.longitude,
      requestPayload.targetDate,
      requestPayload.days
    );

    // Check cache first
    const cached = await getCachedWeatherForecast(lat, lon, targetDate, days);

    if (cached) {
      console.log('[Weather] Using cached forecast for', requestPayload.city || `${requestPayload.latitude},${requestPayload.longitude}`);
      return cached;
    }

    // Fetch with retry logic
    const callGetWeatherForecast = httpsCallable<WeatherForecastRequest, WeatherForecast>(
      functions,
      'getWeatherForecast'
    );

    const result = await retryWithBackoff(
      async () => {
        // Protect UI from hanging callable requests.
        const response = await withTimeout(callGetWeatherForecast(requestPayload), 7000, 'getWeatherForecast');
        if (!response.data) {
          throw new Error('Empty response from weather API');
        }
        return response.data;
      },
      {
        maxRetries: 3,
        initialDelay: 1000,
      }
    );

    // If the backend returns an "empty" forecast for a future targetDate
    // (e.g., OpenWeather 5-day window doesn't cover the trip), fall back
    // to Open-Meteo using the requested date range.
    if (
      result &&
      requestPayload.targetDate &&
      Array.isArray((result as any).forecast) &&
      (result as any).forecast.length === 0
    ) {
      const targetMs = Date.parse(`${requestPayload.targetDate}T00:00:00Z`);
      if (Number.isFinite(targetMs) && targetMs > Date.now()) {
        const ranged = await fetchOpenMeteoForecast(requestPayload).catch(() => null);
        if (ranged) {
          try {
            await cacheWeatherForecast(
              requestPayload.latitude,
              requestPayload.longitude,
              ranged,
              requestPayload.targetDate,
              requestPayload.days
            );
          } catch {
            // ignore cache failures
          }
          return ranged;
        }
      }
    }

    // Cache the result
    if (result) {
      await cacheWeatherForecast(
        requestPayload.latitude,
        requestPayload.longitude,
        result,
        requestPayload.targetDate,
        requestPayload.days
      );
    }

    return result;
  } catch (error: any) {
    console.error('[Weather] Error fetching weather forecast:', error);
    
    // Provide more detailed error information
    const errorMessage = error?.message || 'Unknown error';
    const errorCode = error?.code || 'unknown';
    
    // Log detailed error for debugging
    console.error('[Weather] Error details:', {
      code: errorCode,
      message: errorMessage,
      request: {
        latitude: requestPayload.latitude,
        longitude: requestPayload.longitude,
        city: requestPayload.city,
        countryCode: requestPayload.countryCode,
      },
    });

    // Fallback: use Open-Meteo (no API key) for basic daily temps so the UI can still show weather.
    const fallback = await fetchOpenMeteoForecast(requestPayload).catch(() => null);
    if (fallback) {
      try {
        const { lat: fallbackLat, lon: fallbackLon, targetDate: fallbackTarget, days: fallbackDays } = normalizeWeatherCacheParams(
          requestPayload.latitude,
          requestPayload.longitude,
          requestPayload.targetDate,
          requestPayload.days
        );
        await cacheWeatherForecast(fallbackLat, fallbackLon, fallback, fallbackTarget, fallbackDays);
      } catch {
        // ignore cache failures
      }
      return fallback;
    }

    return null;
  }
};

const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
  let t: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        t = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
      }),
    ]);
  } finally {
    if (t) clearTimeout(t);
  }
};

export type OpenMeteoCurrentWeather = {
  temperature: number;
  condition: string;
  description: string;
};

export type OpenMeteoDailyWeather = {
  dateKey: string;
  tempHigh: number;
  tempLow: number;
  condition: string;
  description: string;
};

/** Live conditions from Open-Meteo (timezone-aware, no API key). */
export const fetchOpenMeteoCurrentWeather = async (
  latitude: number,
  longitude: number,
): Promise<OpenMeteoCurrentWeather | null> => {
  try {
    const url =
      `https://api.open-meteo.com/v1/forecast?latitude=${encodeURIComponent(String(latitude))}` +
      `&longitude=${encodeURIComponent(String(longitude))}` +
      `&current_weather=true&timezone=auto`;
    const response = await withTimeout(fetch(url), 7000, 'open-meteo-current');
    if (!response.ok) return null;
    const json: any = await response.json();
    const current = json?.current_weather;
    if (typeof current?.temperature !== 'number') return null;
    const mapped = mapOpenMeteoCode(
      typeof current.weathercode === 'number' ? current.weathercode : 0,
    );
    return {
      temperature: Math.round(current.temperature),
      condition: mapped.condition,
      description: mapped.description,
    };
  } catch {
    return null;
  }
};

/** Daily highs/lows from Open-Meteo for a destination-local date range. */
export const fetchOpenMeteoDailyWeather = async (
  latitude: number,
  longitude: number,
  startDateKey: string,
  days: number,
): Promise<OpenMeteoDailyWeather[]> => {
  try {
    const startMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startDateKey);
    if (!startMatch) return [];
    const start = new Date(Date.UTC(Number(startMatch[1]), Number(startMatch[2]) - 1, Number(startMatch[3])));
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + Math.max(0, days - 1));
    const format = (date: Date) =>
      `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
    const url =
      `https://api.open-meteo.com/v1/forecast?latitude=${encodeURIComponent(String(latitude))}` +
      `&longitude=${encodeURIComponent(String(longitude))}` +
      `&daily=temperature_2m_max,temperature_2m_min,weathercode` +
      `&start_date=${encodeURIComponent(format(start))}` +
      `&end_date=${encodeURIComponent(format(end))}` +
      `&timezone=auto`;
    const response = await withTimeout(fetch(url), 7000, 'open-meteo-daily');
    if (!response.ok) return [];
    const json: any = await response.json();
    const times: string[] = json?.daily?.time || [];
    const highs: number[] = json?.daily?.temperature_2m_max || [];
    const lows: number[] = json?.daily?.temperature_2m_min || [];
    const codes: number[] = json?.daily?.weathercode || [];
    return times.map((dateKey, index) => {
      const mapped = mapOpenMeteoCode(typeof codes[index] === 'number' ? codes[index] : 0);
      return {
        dateKey,
        tempHigh: Math.round(highs[index] ?? 0),
        tempLow: Math.round(lows[index] ?? highs[index] ?? 0),
        condition: mapped.condition,
        description: mapped.description,
      };
    });
  } catch {
    return [];
  }
};

const mapOpenMeteoCode = (code: number): { condition: string; description: string } => {
  // https://open-meteo.com/en/docs#weathervariables
  if (code === 0) return { condition: 'Clear', description: 'Clear sky' };
  if (code === 1) return { condition: 'Mostly clear', description: 'Mainly clear' };
  if (code === 2) return { condition: 'Partly cloudy', description: 'Partly cloudy' };
  if (code === 3) return { condition: 'Cloudy', description: 'Overcast' };
  if ([45, 48].includes(code)) return { condition: 'Fog', description: 'Fog' };
  if ([51, 53, 55].includes(code)) return { condition: 'Drizzle', description: 'Drizzle' };
  if ([56, 57].includes(code)) return { condition: 'Freezing drizzle', description: 'Freezing drizzle' };
  if ([61, 63, 65].includes(code)) return { condition: 'Rain', description: 'Rain' };
  if ([66, 67].includes(code)) return { condition: 'Freezing rain', description: 'Freezing rain' };
  if ([71, 73, 75, 77].includes(code)) return { condition: 'Snow', description: 'Snow' };
  if ([80, 81, 82].includes(code)) return { condition: 'Rain showers', description: 'Rain showers' };
  if ([85, 86].includes(code)) return { condition: 'Snow showers', description: 'Snow showers' };
  if ([95, 96, 99].includes(code)) return { condition: 'Thunderstorm', description: 'Thunderstorm' };
  return { condition: 'Unknown', description: 'Weather' };
};

const pad2 = (n: number) => String(n).padStart(2, '0');

const formatDateYYYYMMDD = (d: Date) =>
  `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;

const parseYYYYMMDDToUtcDate = (s: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || '').trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const da = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, da));
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const addDaysUtc = (d: Date, days: number) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days));

/**
 * Best-effort Open-Meteo "estimated for trip dates" forecast.
 *
 * - If `targetDate` is provided: request that exact daily range (start_date/end_date).
 * - If Open-Meteo forecast cannot cover that far in the future: fall back to the
 *   archive API for last year's same date range and map dates onto the target year.
 */
const fetchOpenMeteoForecast = async (request: WeatherForecastRequest): Promise<WeatherForecast | null> => {
  const requestedDays = Math.max(1, Math.min(request.days ?? 5, 16));

  const targetStart = request.targetDate ? parseYYYYMMDDToUtcDate(request.targetDate) : null;
  const startDate = targetStart ?? new Date();
  const endDate = addDaysUtc(startDate, requestedDays - 1);

  const dailyVars = ['temperature_2m_max', 'temperature_2m_min', 'weathercode', 'precipitation_probability_max'].join(
    ','
  );

  // Prefer a ranged forecast when targetDate is provided.
  const forecastUrl =
    `https://api.open-meteo.com/v1/forecast` +
    `?latitude=${encodeURIComponent(String(request.latitude))}` +
    `&longitude=${encodeURIComponent(String(request.longitude))}` +
    `&daily=${encodeURIComponent(dailyVars)}` +
    (targetStart
      ? `&start_date=${encodeURIComponent(formatDateYYYYMMDD(startDate))}` +
        `&end_date=${encodeURIComponent(formatDateYYYYMMDD(endDate))}`
      : `&current_weather=true&forecast_days=${encodeURIComponent(String(Math.max(1, Math.min(requestedDays, 10))))}`) +
    `&timezone=auto`;

  const tryParseDaily = async (res: Response) => {
    if (!res.ok) return null;
    const json: any = await res.json();
    const daily = json?.daily;
    const times: string[] = daily?.time || [];
    const tMax: number[] = daily?.temperature_2m_max || [];
    const tMin: number[] = daily?.temperature_2m_min || [];
    const codes: number[] = daily?.weathercode || [];
    const precip: number[] = daily?.precipitation_probability_max || [];
    if (!times.length) return null;
    return { json, times, tMax, tMin, codes, precip };
  };

  // 1) Try Open-Meteo forecast for the requested range
  const rangedRes = await withTimeout(fetch(forecastUrl), 7000, 'open-meteo');
  let parsed = await tryParseDaily(rangedRes);

  // 2) If targetDate is too far for forecast, fall back to last year's archive for same range.
  let isSeasonalEstimate = false;
  if (!parsed && targetStart) {
    const lastYearStart = new Date(
      Date.UTC(targetStart.getUTCFullYear() - 1, targetStart.getUTCMonth(), targetStart.getUTCDate())
    );
    const lastYearEnd = addDaysUtc(lastYearStart, requestedDays - 1);

    const archiveUrl =
      `https://archive-api.open-meteo.com/v1/archive` +
      `?latitude=${encodeURIComponent(String(request.latitude))}` +
      `&longitude=${encodeURIComponent(String(request.longitude))}` +
      `&daily=${encodeURIComponent('temperature_2m_max,temperature_2m_min,weathercode')}` +
      `&start_date=${encodeURIComponent(formatDateYYYYMMDD(lastYearStart))}` +
      `&end_date=${encodeURIComponent(formatDateYYYYMMDD(lastYearEnd))}` +
      `&timezone=auto`;

    const archiveRes = await withTimeout(fetch(archiveUrl), 7000, 'open-meteo-archive');
    const archiveParsed = await tryParseDaily(archiveRes);
    if (archiveParsed) {
      // Map archive dates onto target date range (same month/day span).
      const mappedTimes: string[] = [];
      for (let i = 0; i < archiveParsed.times.length; i++) {
        mappedTimes.push(formatDateYYYYMMDD(addDaysUtc(targetStart, i)));
      }
      parsed = {
        ...archiveParsed,
        times: mappedTimes,
        // precip isn't available in archive response; use empty array.
        precip: [],
      };
      isSeasonalEstimate = true;
    }
  }

  if (!parsed) return null;

  const { times, tMax, tMin, codes, precip, json } = parsed;

  const current = json?.current_weather;
  const currentCode = typeof current?.weathercode === 'number' ? current.weathercode : (codes[0] ?? 0);
  const currentMapped = mapOpenMeteoCode(currentCode);
  const fallbackTemp = (tMax[0] ?? tMin[0] ?? 0) as number;
  const currentTemp = typeof current?.temperature === 'number' ? current.temperature : fallbackTemp;
  const currentWind = typeof current?.windspeed === 'number' ? current.windspeed : 0;
  const currentWindDir = typeof current?.winddirection === 'number' ? current.winddirection : 0;

  const forecast = times.slice(0, requestedDays).map((date: string, i: number) => {
    const code = typeof codes[i] === 'number' ? codes[i] : currentCode;
    const mapped = mapOpenMeteoCode(code);
    const max = typeof tMax[i] === 'number' ? tMax[i] : fallbackTemp;
    const min = typeof tMin[i] === 'number' ? tMin[i] : fallbackTemp;
    const prob = typeof precip[i] === 'number' ? precip[i] / 100 : 0;
    return {
      date,
      temperature: {
        min,
        max,
        day: max,
        night: min,
      },
      condition: isSeasonalEstimate ? `${mapped.condition} (Seasonal estimate)` : mapped.condition,
      description: isSeasonalEstimate ? `${mapped.description} (Seasonal estimate)` : mapped.description,
      icon: '',
      humidity: 0,
      windSpeed: currentWind,
      precipitation: {
        probability: Math.max(0, Math.min(1, prob)),
      },
      sunrise: undefined,
      sunset: undefined,
    };
  });

  // For ranged requests without current_weather, use the first day as the "current" display.
  const currentFromForecast = forecast[0];
  const currentDisplayTemp =
    typeof currentTemp === 'number' && Number.isFinite(currentTemp) ? currentTemp : currentFromForecast?.temperature?.day ?? fallbackTemp;

  return {
    location: {
      city: request.city || 'Destination',
      country: request.countryCode || '',
      latitude: request.latitude,
      longitude: request.longitude,
    },
    current: {
      temperature: Math.round(currentDisplayTemp),
      feelsLike: Math.round(currentDisplayTemp),
      condition: currentFromForecast?.condition || currentMapped.condition,
      description: currentFromForecast?.description || currentMapped.description,
      icon: '',
      humidity: 0,
      windSpeed: currentWind,
      windDirection: currentWindDir,
      pressure: 0,
    },
    forecast,
  };
};

/**
 * Get coordinates from destination name using OpenWeatherMap geocoding (fallback)
 */
export const geocodeWithOpenWeather = async (
  destination: string,
  options?: { country?: string },
): Promise<{ latitude: number; longitude: number; city: string; country: string } | null> => {
  const countryCode = options?.country || getCountryCodeFromDestination(destination);

  try {
    const cached = await getCachedGeocode(destination);
    if (cached && coordsWithinCountryBounds(countryCode, cached.latitude, cached.longitude)) {
      console.log('[Weather] Using cached geocode for', destination);
      return cached;
    }

    // Call the Cloud Function which has access to the OpenWeatherMap API key
    const { httpsCallable, functions } = await import('@/lib/functions');
    const geocodeFunction = httpsCallable<{ query: string }, { 
      lat: number; 
      lon: number; 
      name: string; 
      country: string;
    }>(functions, 'geocodeLocation');
    
    const result = await retryWithBackoff(
      async () => {
        const response = await geocodeFunction({ query: destination });
        if (!response.data || !response.data.lat || !response.data.lon) {
          throw new Error('Invalid geocoding response');
        }
        return response.data;
      },
      {
        maxRetries: 2, // Fewer retries for geocoding
        initialDelay: 500,
      }
    );

    if (result) {
      const geocodeResult = {
        latitude: result.lat,
        longitude: result.lon,
        city: result.name,
        country: result.country,
      };

      if (!coordsWithinCountryBounds(countryCode, geocodeResult.latitude, geocodeResult.longitude)) {
        console.warn('[Weather] Rejected OpenWeather geocode outside expected country for', destination);
        return null;
      }

      await cacheGeocode(
        destination,
        geocodeResult.latitude,
        geocodeResult.longitude,
        geocodeResult.city,
        geocodeResult.country,
      );

      return geocodeResult;
    }
    
    return null;
  } catch (error: any) {
    const errorMessage = error?.message || 'Unknown error';
    const errorCode = error?.code || 'unknown';
    console.error('[Weather] OpenWeatherMap geocoding failed:', {
      destination,
      code: errorCode,
      message: errorMessage,
    });
    return null;
  }
};

/**
 * Get coordinates from destination name using geocoding
 * Tries Mapbox first, then falls back to OpenWeatherMap
 * Uses caching and retry logic for improved reliability
 */
export const getDestinationCoordinates = async (
  destination: string,
  options?: { country?: string },
): Promise<{ latitude: number; longitude: number; city: string; country: string } | null> => {
  const countryCode = options?.country || getCountryCodeFromDestination(destination);

  const isValidWeatherGeocode = (latitude: number, longitude: number): boolean =>
    coordsWithinCountryBounds(countryCode, latitude, longitude);

  try {
    const cached = await getCachedGeocode(destination);
    if (cached && isValidWeatherGeocode(cached.latitude, cached.longitude)) {
      console.log('[Weather] Using cached coordinates for', destination);
      return cached;
    }

    // Add timeout to prevent hanging
    const timeoutPromise = new Promise<null>((resolve) => {
      setTimeout(() => {
        console.warn('[Weather] getDestinationCoordinates timeout for', destination);
        resolve(null);
      }, 5000); // 5 second timeout
    });

    const geocodePromise = (async () => {
      // Try Mapbox first (if available)
      try {
        const { geocodeAddress } = await import('./maps');
        const mapboxPromise = geocodeAddress(
          destination,
          countryCode ? { country: countryCode } : undefined,
        );
        const mapboxTimeout = new Promise<null>((resolve) => {
          setTimeout(() => resolve(null), 3000); // 3 second timeout for Mapbox
        });

        const result = await Promise.race([mapboxPromise, mapboxTimeout]);

        if (result && result.coordinates && result.coordinates.length >= 2) {
          const latitude = result.coordinates[1];
          const longitude = result.coordinates[0];
          if (
            !isValidWeatherGeocode(latitude, longitude) ||
            !geocodeContextMatchesCountryCode(result.context, countryCode)
          ) {
            console.warn('[Weather] Rejected geocode outside expected country for', destination);
            return null;
          }

          const geocodeResult = {
            latitude,
            longitude,
            city: result.placeName?.split(',')[0] || destination,
            country: result.context?.find((ctx) => ctx.id?.startsWith('country'))?.text || '',
          };

          await cacheGeocode(
            destination,
            geocodeResult.latitude,
            geocodeResult.longitude,
            geocodeResult.city,
            geocodeResult.country,
          );

          return geocodeResult;
        }
      } catch (mapboxError: any) {
        console.warn('[Weather] Mapbox geocoding failed, trying OpenWeatherMap:', {
          destination,
          error: mapboxError?.message || 'Unknown error',
        });
      }

      const openWeatherResult = await geocodeWithOpenWeather(destination, { country: countryCode });
      if (openWeatherResult && isValidWeatherGeocode(openWeatherResult.latitude, openWeatherResult.longitude)) {
        return openWeatherResult;
      }

      console.warn(`[Weather] Could not geocode "${destination}" with any service`);
      return null;
    })();

    const result = await Promise.race([geocodePromise, timeoutPromise]);
    return result;
  } catch (error: any) {
    console.error('[Weather] Error geocoding destination:', {
      destination,
      error: error?.message || 'Unknown error',
      code: error?.code,
    });
    return null;
  }
};

